import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaWhatsapp, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { useUser } from '../contexts/UserContext';
import ButtonWithLoading from '../components/ui/ButtonWithLoading';
import { getApiBaseUrl } from '../config/api';

/**
 * WhatsApp Linking Page
 *
 * This page handles the linking of a WhatsApp number to an existing OmniButler account.
 * It's accessed via a special link sent to the user via WhatsApp, containing a token.
 */
const WhatsAppLinking: React.FC = () => {
  const { currentUser, appUser, loading: userLoading } = useUser();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [_linking, setLinking] = useState(false);
  const [linkingStatus, setLinkingStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Extract token from URL when component mounts
  useEffect(() => {
    if (!token) {
      setLinkingStatus('error');
      setErrorMessage('No linking token provided. Please use the link from your WhatsApp message.');
    }
  }, [token]);

  // If user is already authenticated, proceed with linking process
  useEffect(() => {
    const linkWhatsAppAccount = async () => {
      if (currentUser && appUser && token && linkingStatus === 'idle') {
        try {
          setLinking(true);
          console.log('Linking WhatsApp with token:', token);

          // Get Firebase ID token
          const idToken = await currentUser.getIdToken();

          // Get the API base URL
          const apiBaseUrl = getApiBaseUrl();
          console.log('Using API base URL:', apiBaseUrl);

          // Call API to link WhatsApp to user account
          const response = await fetch(`${apiBaseUrl}/api/v1/whatsapp/linking/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ token }),
          });

          console.log('API response status:', response.status);

          if (!response.ok) {
            const responseText = await response.text();
            console.error('Error response text:', responseText);

            let errorData;
            try {
              errorData = JSON.parse(responseText);
            } catch (e) {
              console.error('Failed to parse error response as JSON:', e);
              throw new Error(
                `Failed to link WhatsApp account: ${response.status} ${response.statusText}`
              );
            }

            throw new Error(
              errorData.detail || errorData.message || 'Failed to link WhatsApp account'
            );
          }

          setLinkingStatus('success');

          // Redirect to dashboard after successful linking
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000); // Redirect after 2 seconds to allow user to see success message
        } catch (error) {
          console.error('Error linking WhatsApp:', error);
          setLinkingStatus('error');
          setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
        } finally {
          setLinking(false);
        }
      }
    };

    // Only attempt linking if user is logged in and not in error state
    if (!userLoading && currentUser && appUser && linkingStatus === 'idle') {
      linkWhatsAppAccount();
    }
  }, [currentUser, appUser, token, userLoading, linkingStatus, navigate]);

  const handleLogin = () => {
    navigate('/login', { state: { from: { pathname: `/whatsapp-link?token=${token}` } } });
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  // Show different UI states based on linking process
  const renderContent = () => {
    // If user is not logged in, show login prompt
    if (!userLoading && !appUser) {
      return (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Link Your WhatsApp Account</h2>
          <p className="mb-8">
            Please sign in with your Google account to link your WhatsApp to OmniButler.
          </p>
          <ButtonWithLoading
            onClick={handleLogin}
            loading={false}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
          >
            Sign In with Google
          </ButtonWithLoading>
        </div>
      );
    }

    // If linking is successful
    if (linkingStatus === 'success') {
      return (
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 p-3 rounded-full">
              <FaCheck className="text-green-600 text-3xl" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-4">WhatsApp Successfully Linked!</h2>
          <p className="mb-8">
            Your WhatsApp account has been successfully linked to your OmniButler account. You can
            now use WhatsApp to interact with OmniButler.
          </p>
          <p className="text-sm text-gray-500 mb-4">Redirecting to dashboard...</p>
          <div className="animate-pulse">
            <ButtonWithLoading
              onClick={handleGoToDashboard}
              loading={false}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
            >
              Go to Dashboard
            </ButtonWithLoading>
          </div>
        </div>
      );
    }

    // If there's an error
    if (linkingStatus === 'error') {
      return (
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <FaExclamationTriangle className="text-red-600 text-3xl" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-4">Linking Failed</h2>
          <p className="text-red-600 mb-8">{errorMessage}</p>
          <ButtonWithLoading
            onClick={() => navigate('/')}
            loading={false}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
          >
            Go to Home Page
          </ButtonWithLoading>
        </div>
      );
    }

    // Default: Show loading state
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Linking Your WhatsApp Account</h2>
        <p className="mb-8">Please wait while we link your WhatsApp account to OmniButler...</p>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Link WhatsApp - OmniButler</title>
      </Helmet>
      <section className="py-16 min-h-screen flex items-center bg-gradient-to-b from-indigo-50 to-blue-100 dark:from-slate-900 dark:to-slate-800">
        <div className="container mx-auto px-4">
          <div className="max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <div className="bg-green-500 p-3 rounded-full">
                <FaWhatsapp className="text-white text-4xl" />
              </div>
            </div>
            {renderContent()}
          </div>
        </div>
      </section>
    </>
  );
};

export default WhatsAppLinking;
